import React, { useEffect, useState } from "react";
import purchaseAPI, {
  Purchase,
  CreatePurchaseData,
  UpdatePurchaseData,
} from "../../services/purchaseAPI";
import supplierAPI from "../../services/supplierAPI";
import { branchAPI } from "../../services/branchAPI";
import { useAuthStore } from "../../stores/authStore";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import TableActionButton from "../../components/layout/TableActionButton";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { useRBAC } from "../../hooks/useRBAC";
import { useTradeTerminology } from "../../utils/tradeTerminology";

const purchaseSchema = yup.object({
  supplierId: yup.string().required("Supplier is required"),
  branchId: yup.string().nullable().optional(),
});

type PurchaseFormData = yup.InferType<typeof purchaseSchema>;

const PurchasesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { terms } = useTradeTerminology();
  const { user } = useAuthStore();
  const role = String(user?.role || "").toUpperCase();
  const isSuperAdmin = role === "SUPER_ADMIN" || role === "SUPERADMIN" || role === "PLATFORM_ADMIN";
  const { canCreate, canUpdate, canDelete, hasAnyRole } = useRBAC();
  const canCreatePurchase = canCreate("purchases") || canCreate("inventory") || hasAnyRole("SUPER_ADMIN", "ADMIN");
  const canUpdatePurchase = canUpdate("purchases") || canUpdate("inventory") || hasAnyRole("SUPER_ADMIN", "ADMIN");
  const canDeletePurchase = canDelete("purchases") || canDelete("inventory") || hasAnyRole("SUPER_ADMIN", "ADMIN");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [supplierFilter, setSupplierFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<Purchase | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    purchaseId: string | null;
  }>({
    open: false,
    purchaseId: null,
  });
  const [returnDialog, setReturnDialog] = useState<{
    open: boolean;
    purchase: Purchase | null;
    reason: string;
  }>({
    open: false,
    purchase: null,
    reason: "",
  });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const { sortBy, sortOrder, handleSort, sortParams } = useTableSort<
    "purchaseNumber" | "supplier" | "branch" | "totalAmount" | "status" | "createdAt"
  >("createdAt", "desc");

  // Fetch branches for dropdown
  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliersAll"],
    queryFn: () => supplierAPI.getAllSuppliers(),
  });

  const { data: purchasesData } = useQuery({
    queryKey: [
      "purchases",
      page,
      rowsPerPage,
      debouncedSearch,
      branchFilter,
      supplierFilter,
      statusFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      purchaseAPI.getPurchases({
        pageSize: rowsPerPage,
        pageNo: page + 1,
        search: debouncedSearch || undefined,
        branchId: branchFilter !== "all" ? branchFilter : undefined,
        supplierId: supplierFilter !== "all" ? supplierFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        ...sortParams,
      }),
  });

  const onSort = (
    field: "purchaseNumber" | "supplier" | "branch" | "totalAmount" | "status" | "createdAt"
  ) => handleSort(field, () => setPage(0));

  const { mutate: createPurchase } = useMutation({
    mutationFn: (data: CreatePurchaseData) => purchaseAPI.createPurchase(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      setAlert({ type: "success", message: "Purchase created successfully!" });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to create purchase",
      });
    },
  });

  const { mutate: updatePurchase } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePurchaseData }) =>
      purchaseAPI.updatePurchase(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      setAlert({ type: "success", message: "Purchase updated successfully!" });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to update purchase",
      });
    },
  });

  const { mutate: deletePurchase } = useMutation({
    mutationFn: (id: string) => purchaseAPI.deletePurchase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      setAlert({ type: "success", message: "Purchase deleted successfully!" });
      setDeleteDialog({ open: false, purchaseId: null });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to delete purchase",
      });
    },
  });

  const { mutate: receivePurchase } = useMutation({
    mutationFn: (id: string) =>
      purchaseAPI.updatePurchase(id, { status: "received" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      setAlert({ type: "success", message: "Purchase received successfully!" });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to receive purchase",
      });
    },
  });

  const { mutate: cancelPurchase } = useMutation({
    mutationFn: (id: string) => purchaseAPI.cancelPurchase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      setAlert({
        type: "success",
        message: "Purchase cancelled successfully!",
      });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to cancel purchase",
      });
    },
  });

  const { mutate: returnPurchase, isPending: returning } = useMutation({
    mutationFn: ({
      id,
      reason,
    }: {
      id: string;
      reason?: string;
    }) => purchaseAPI.returnPurchase(id, { reason }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["purchases"] });
      setReturnDialog({ open: false, purchase: null, reason: "" });
      setAlert({
        type: "success",
        message: `Purchase returned. Debit note ${result.debitNote?.debitNoteNumber || ""} created.`,
      });
    },
    onError: (error: any) => {
      setAlert({
        type: "error",
        message:
          error.response?.data?.message || "Failed to return purchase",
      });
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PurchaseFormData>({
    resolver: yupResolver(purchaseSchema),
    defaultValues: {
      supplierId: "",
      branchId: "",
    },
  });

  const handleOpenDialog = (purchase?: Purchase) => {
    if (purchase) {
      setEditingPurchase(purchase);
      reset({
        supplierId: purchase.supplierId || "",
        branchId: purchase.branchId,
      });
    } else {
      setEditingPurchase(null);
      reset({
        supplierId: "",
        branchId: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPurchase(null);
    reset();
  };

  const onSubmit = (data: PurchaseFormData) => {
    if (editingPurchase) {
      updatePurchase({
        id: editingPurchase.id,
        data: { supplierId: data.supplierId },
      });
    } else {
      createPurchase({
        supplierId: data.supplierId,
        branchId: data.branchId ? data.branchId : null,
        items: [],
        totalAmount: 0,
        taxAmount: 0,
        netAmount: 0,
      });
    }
  };

  const handleDelete = (id: string) => {
    setDeleteDialog({ open: true, purchaseId: id });
  };

  const confirmDelete = () => {
    if (deleteDialog.purchaseId) {
      deletePurchase(deleteDialog.purchaseId);
    }
  };

  const handleReceivePurchase = (id: string) => {
    receivePurchase(id);
  };

  const handleCancelPurchase = (id: string) => {
    cancelPurchase(id);
  };

  const handleOpenReturn = (purchase: Purchase) => {
    setReturnDialog({
      open: true,
      purchase,
      reason: `Return ${purchase.purchaseNumber}`,
    });
  };

  const confirmReturn = () => {
    if (!returnDialog.purchase) return;
    returnPurchase({
      id: returnDialog.purchase.id,
      reason: returnDialog.reason.trim() || undefined,
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "received":
        return "success";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const filterDropdowns = (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" sx={{ width: "100%" }}>
      {isSuperAdmin && (
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
          <InputLabel>Branch</InputLabel>
          <Select
            value={branchFilter}
            label="Branch"
            onChange={(e) => {
              setBranchFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Branches</MenuItem>
            {branches.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
        <InputLabel>Supplier</InputLabel>
        <Select
          value={supplierFilter}
          label="Supplier"
          onChange={(e) => {
            setSupplierFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Suppliers</MenuItem>
          {suppliers.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="received">Received</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title={terms.purchasesLabel}
        subtitle="Manage vendor purchase orders, inventory restocking, and returns"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: terms.purchasesLabel },
        ]}
        action={
          canCreatePurchase ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {terms.purchaseAddButton}
            </Button>
          ) : undefined
        }
      />

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <DataListCard
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        searchPlaceholder="Search purchase #, vendor invoice, notes..."
        filters={filterDropdowns}
      >
        <TableContainer>
          <Table size="small" sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <SortableTableCell label="Purchase Number" field="purchaseNumber" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Supplier" field="supplier" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Branch" field="branch" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Total Amount" field="totalAmount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <SortableTableCell label="Created At" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchasesData?.data.map((purchase: Purchase) => (
                <TableRow key={purchase.id}>
                  <TableCell>{purchase.purchaseNumber}</TableCell>
                  <TableCell>{purchase.supplier?.name || purchase.supplierName || "—"}</TableCell>
                  <TableCell>
                    {purchase.branch?.name || (purchase.branchId ? purchase.branchId : "Direct Company")}
                  </TableCell>
                  <TableCell>
                    ₹{Number(purchase.totalAmount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip
                        label={purchase.status.toUpperCase()}
                        color={getStatusColor(purchase.status)}
                        size="small"
                      />
                      {purchase.debitNotes && purchase.debitNotes.length > 0 && (
                        <Tooltip
                          title={purchase.debitNotes
                            .map((dn) => `#${dn.debitNoteNumber} (₹${dn.netAmount})`)
                            .join(", ")}
                        >
                          <Chip
                            label="Debit Note"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 700, fontSize: "0.68rem", height: 22 }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {new Date(purchase.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {canUpdatePurchase && purchase.status === "pending" && (
                        <TableActionButton
                          title="Receive purchase"
                          variant="success"
                          onClick={() => handleReceivePurchase(purchase.id)}
                        >
                          <AddIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                      )}
                      {canUpdatePurchase && (
                        <TableActionButton
                          title="Edit purchase"
                          variant="primary"
                          onClick={() => handleOpenDialog(purchase)}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                      )}
                      {canDeletePurchase && purchase.status === "pending" && (
                        <TableActionButton
                          title="Cancel purchase"
                          variant="warning"
                          onClick={() => handleCancelPurchase(purchase.id)}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                      )}
                      {canUpdatePurchase && purchase.status !== "cancelled" && (
                        <TableActionButton
                          title="Return purchase (debit note)"
                          variant="warning"
                          onClick={() => handleOpenReturn(purchase)}
                        >
                          <UndoIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                      )}
                      {canDeletePurchase && (
                        <TableActionButton
                          title="Delete purchase"
                          variant="danger"
                          onClick={() => handleDelete(purchase.id)}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </TableActionButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={purchasesData?.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </DataListCard>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {editingPurchase ? `Edit ${terms.purchasesLabel}` : terms.purchaseAddButton}
              </Typography>
              <IconButton size="small" onClick={handleCloseDialog}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: "72vh" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="supplierId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.supplierId}>
                      <InputLabel>Supplier</InputLabel>
                      <Select {...field} label="Supplier">
                        <MenuItem value="">
                          <em>Select supplier</em>
                        </MenuItem>
                        {suppliers.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.supplierId && (
                        <FormHelperText>{errors.supplierId.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {editingPurchase ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Branch"
                    value={
                      editingPurchase.branch?.name || editingPurchase.branchId
                    }
                    disabled
                  />
                </Grid>
              ) : (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="branchId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.branchId}>
                        <InputLabel>Branch (Optional)</InputLabel>
                        <Select {...field} value={field.value || ""} label="Branch (Optional)">
                          <MenuItem value="">
                            <em>None (Direct Company)</em>
                          </MenuItem>
                          {branches.map((branch: { id: string; name: string }) => (
                            <MenuItem key={branch.id} value={branch.id}>
                              {branch.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.branchId && (
                          <FormHelperText>
                            {errors.branchId.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPurchase ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, purchaseId: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this purchase?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, purchaseId: null })}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase return → debit note */}
      <Dialog
        open={returnDialog.open}
        onClose={() =>
          setReturnDialog({ open: false, purchase: null, reason: "" })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>Return purchase</Typography>
            <IconButton size="small" onClick={() => setReturnDialog({ open: false, purchase: null, reason: "" })}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "72vh" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates debit note and reverses stock for{" "}
            <strong>{returnDialog.purchase?.purchaseNumber}</strong>. Purchase
            status becomes cancelled.
          </Typography>
          <TextField
            fullWidth
            label="Reason"
            value={returnDialog.reason}
            onChange={(e) =>
              setReturnDialog((prev) => ({ ...prev, reason: e.target.value }))
            }
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setReturnDialog({ open: false, purchase: null, reason: "" })
            }
            disabled={returning}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmReturn}
            color="warning"
            variant="contained"
            disabled={returning}
          >
            {returning ? "Returning…" : "Confirm return"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchasesPage;
